import { useState, useEffect } from 'react';
import { DocumentTextIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Endpoint } from '@/types';
import { CompactEndpointConfig } from './CompactEndpointConfig';
import { SchemaBuilder } from '../schema-builder/SchemaBuilder';
import { UserName } from '../ui/UserName';

interface EndpointOverviewTabProps {
  endpoint: Endpoint;
  isEditing?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  /**
   * Render the Request (Input) / Response (Output) schema cards (task 785).
   *
   * Two hubs share this tab, and they disagree about which cards to use. The
   * AI Service hub (`/services/:id`) renders `ServiceSchemaCards`, whose cards
   * each own a read → edit → save cycle (task 785; back on Overview since task
   * 882), so it passes `false` and these read-only copies stay off — one pair
   * per page, not two. The legacy endpoint hub (`/endpoints/:id`) has no such
   * component — its Overview IS the schema editing surface, driven by the
   * page's bulk `isEditing` mode — so the default keeps the cards and that hub
   * is untouched.
   */
  showSchemas?: boolean;
}

export function EndpointOverviewTab({
  endpoint,
  isEditing = false,
  onFieldChange,
  showSchemas = true
}: EndpointOverviewTabProps) {
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);

  // Expand metadata when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setIsMetadataExpanded(true);
    }
  }, [isEditing]);

  return (
    <div className="space-y-6">
      {/* Description Card - at top, minimized height */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Description</h2>
        {isEditing ? (
          <textarea
            value={endpoint.description || ''}
            onChange={(e) => onFieldChange?.('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Enter endpoint description"
          />
        ) : endpoint.description ? (
          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {endpoint.description}
          </div>
        ) : (
          <div className="text-sm text-gray-400 dark:text-gray-500 italic">
            No description provided
          </div>
        )}
      </div>

      {/* Request Section */}
      <CompactEndpointConfig
        endpoint={endpoint}
        isEditing={isEditing}
        onFieldChange={onFieldChange}
      />

      {/* Request/Response schemas - side by side. The AI Service hub renders
          the editable `ServiceSchemaCards` instead (task 785); see
          `showSchemas`. */}
      {showSchemas && (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Input Schema Card - renamed to "Request (Input)" */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {endpoint.input || isEditing ? (
            <SchemaBuilder
              value={endpoint.input}
              onChange={(schema) => onFieldChange?.('input', schema)}
              readOnly={!isEditing}
              label="Request (Input)"
            />
          ) : (
            <>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Request (Input)</h2>
              <div className="text-center py-6">
                <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  No input schema defined
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This endpoint accepts any JSON input. Define a schema to validate requests.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Output Schema Card - renamed to "Response (Output)" */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {endpoint.output || isEditing ? (
            <SchemaBuilder
              value={endpoint.output}
              onChange={(schema) => onFieldChange?.('output', schema)}
              readOnly={!isEditing}
              label="Response (Output)"
            />
          ) : (
            <>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Response (Output)</h2>
              <div className="text-center py-6">
                <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  No output schema defined
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This endpoint returns any JSON output. Define a schema to document the response structure.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {/* Metadata Card - Collapsible, no timestamps (shown in header) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <button
          onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
          className="w-full flex items-center justify-between text-left"
        >
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Metadata</h2>
          {isMetadataExpanded ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {isMetadataExpanded && (
          <dl className="mt-4 space-y-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created By</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                <UserName userId={endpoint.createdBy} />
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}
